import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type PolarisAdminSelectOption = {
    value: string;
    label: string;
};

type PolarisAdminSelectProps = {
    value: string;
    options: PolarisAdminSelectOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    size?: "default" | "compact";
    className?: string;
    "data-testid"?: string;
};

export default function PolarisAdminSelect({
    value,
    options,
    onChange,
    disabled = false,
    placeholder = "Select…",
    size = "default",
    className = "",
    "data-testid": dataTestId,
}: PolarisAdminSelectProps) {
    const generatedId = useId();
    const listboxId = `${generatedId}-listbox`;
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [dropUp, setDropUp] = useState(false);

    const selected = options.find((opt) => opt.value === value);
    const displayLabel = selected?.label ?? placeholder;

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const estimatedHeight = options.length * 36 + 12;
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
        setDropUp(openUp);
        setMenuStyle({
            position: "fixed",
            left: rect.left,
            width: Math.max(rect.width, 140),
            zIndex: 4000,
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 4, bottom: "auto" }),
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);
        return () => {
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reposition when open/options change
    }, [open, options.length]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
            }
        };
        const timer = window.setTimeout(() => {
            document.addEventListener("mousedown", onPointerDown);
        }, 0);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const handleSelect = (next: string) => {
        onChange(next);
        setOpen(false);
    };

    return (
        <div
            className={`pa-select pa-select--${size}${open ? " is-open" : ""}${
                disabled ? " is-disabled" : ""
            }${dropUp ? " is-drop-up" : ""}${className ? ` ${className}` : ""}`}
            ref={rootRef}
            data-testid={dataTestId}
        >
            <button
                type="button"
                ref={triggerRef}
                className="pa-select-trigger"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                onClick={() => {
                    if (disabled) return;
                    setOpen((prev) => !prev);
                }}
            >
                <span className={`pa-select-value${!selected ? " is-placeholder" : ""}`}>
                    {displayLabel}
                </span>
                <span className="pa-select-chevron" aria-hidden="true" />
            </button>

            {open &&
                !disabled &&
                createPortal(
                    <ul
                        id={listboxId}
                        ref={menuRef}
                        className="pa-select-menu"
                        role="listbox"
                        style={menuStyle}
                    >
                        {options.map((opt) => {
                            const isSelected = opt.value === value;
                            return (
                                <li key={opt.value} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`pa-select-option${isSelected ? " is-selected" : ""}`}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        <span className="pa-select-option-label">{opt.label}</span>
                                        {isSelected && (
                                            <span className="pa-select-check" aria-hidden="true">
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body,
                )}
        </div>
    );
}
